/**
 * auth/session — the dashboard token store for the SPA.
 *
 * The token IS the credential (same opaque dashboard token the fleet uses). It lives
 * in localStorage so a reload keeps the session; it is never logged. `subscribe` lets
 * the auth context react when a 401 elsewhere clears it. NOTE: localStorage is
 * readable by any script on this origin — acceptable for a demo operator tool, and the
 * production hardening (HttpOnly session cookie via the backend login form) is tracked
 * as a follow-up.
 */
const KEY = 'tk_dashboard_token';

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export function getToken(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(KEY, token);
  } catch {
    /* storage unavailable (private mode) — session is memory-only this load */
  }
  for (const l of listeners) l(token);
}

export function clearToken(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l(null);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
